import { useContext, useState, useEffect } from 'react'
import Section from './_Section'
import { detectShadeClash, transformPaddingToTailwind } from '../../helpers/style'
import ButtonEl from '../helpers/ctas/Button'
import { CheckIcon } from '@heroicons/react/24/solid'
import PercentValue from '../helpers/PercentValue'
import themeConfig from '../../../theme.config'
import { MarkupContext } from '../../providers/Markup'

function RatesCardList(props) {
  const { createMarkup, detectPaletteClass, getPaletteColor } = useContext(MarkupContext)
  const [highlightColor, setHighlightColor] = useState('secondary')
  const componentPadding = transformPaddingToTailwind(props.componentPadding)
  
  // Handle content alignment (left/center/right) from Component Configuration
  const alignment = props.contentAlignment?.toLowerCase() || 'left'
  const textAlignClass = alignment === 'center' ? 'text-center' : alignment === 'right' ? 'text-right' : 'text-left'

  // this component explicitly uses the secondary shade so if it clashes with the selected background colour we need to change it.
  useEffect(() => {
    if (detectShadeClash(props.backgroundColor, getPaletteColor('secondary'))) {
      setHighlightColor('primary')
    }
  }, [])

  return (
    <Section type="ratesCardList" outerClass={componentPadding} innerClass={`px-8 ${textAlignClass}`} {...props}>
      {props.products?.length ? (
        // max-lg:divide-y-2 lg:divide-x-2 divide-secondary divide-dotted
        <div className="grid grid-cols-1 lg:grid-cols-6 grid-flow-row-dense grid-flow-col grid-rows-[repeat(10,auto)] mt-4 lg:mb-16 gap-y-0 gap-x-0">
          {(props.products || []).map((item, i) => {
            const col = i + 1
            return (
              <div
                className={detectPaletteClass(`grid grid-rows-subgrid
                  lg:nth-[3n+3]:col-start-5
                  lg:nth-[3n+2]:col-start-3
                  lg:nth-[3n+1]:col-start-1
                  lg:[&:last-child:first-child]:col-start-3
                  lg:[&:last-child:nth-child(3n+1)]:col-start-3
                  lg:[&:nth-last-child(2):first-child]:col-start-2
                  lg:[&:nth-last-child(2):nth-child(3n+1)]:col-start-2
                  lg:[&:nth-child(2):last-child]:col-start-4
                  lg:[&:last-child:nth-child(3n+2)]:col-start-4
                  border border-slate-200 rounded-2xl
                  bg-white
                  m-3 py-6 px-4
                  col-span-2 row-span-6 mb-8 lg:mb-16 px-4 max-lg:pt-8
                  shadow-xl`)}
              >
                {/* lg:[&:last-child:nth-child(3n+1)]:border-l-0 
                  lg:[&:nth-last-child(2):nth-child(3n+1)]:border-l-0  */}
                <div className={`font-bold text-lg ${textAlignClass}`}>{item.title}</div>

                {item.description && (
                  <div
                    className={detectPaletteClass(`text-sm mt-2 ${textAlignClass} ${themeConfig.settings.ratesCard.highlightDescription ? `text-${highlightColor}-500` : ''}`)}
                    data-rte="true"
                    dangerouslySetInnerHTML={createMarkup(item.description)}
                  ></div>
                )}

                {item.variants?.length &&
                (item.variants[0]?.interestRate || item.variants[0]?.interestRateDisclaimer) ? (
                  <div className={`mt-6 flex ${alignment === 'center' ? 'justify-center' : alignment === 'right' ? 'justify-end' : 'justify-start'} gap-4 max-sm:flex-col max-sm:gap-4`}>
                    <div
                      className={detectPaletteClass(`${textAlignClass} ${themeConfig.settings.ratesCard.highlightRate ? `text-${highlightColor}-500` : ''}`)}
                    >
                      <PercentValue
                        disclaimer={item.variants[0].interestRateDisclaimer}
                        rate={item.variants[0].interestRate.length > 0 ? item.variants[0].interestRate : 'n/a'}
                        rateType="Interest"
                      />
                    </div>
                    {item.variants[0]?.comparisonRate && (
                      <div className={textAlignClass}>
                        <PercentValue
                          disclaimer={item.variants[0].comparisonRateDisclaimer}
                          rate={item.variants[0].comparisonRate}
                          rateType="Comparison"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  // deals with a product with no variants (and therefore no rates) being set alongside products with rates, causing a vertical layout issue.
                  <div>&nbsp;</div>
                )}

                {item.uniqueSellingPoints?.length ? (
                  <ul className={`mt-10 ${textAlignClass}`}>
                    {item.uniqueSellingPoints.map((item, i) => (
                      <li key={i} className={`mb-4 flex ${alignment === 'center' ? 'justify-center' : alignment === 'right' ? 'justify-end' : 'justify-start'}`}>
                        <CheckIcon role="none" className={detectPaletteClass(`h-6 w-6 mr-4 text-${highlightColor}-500 shrink-0`)} />
                        <span className={`${textAlignClass} inline-block`}>{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}

                <div className={textAlignClass}>
                  {themeConfig.settings.ratesCard.showDisclaimersFromCMS && (
                    <div className={`mt-4 border-t-2 py-4 text-sm ${textAlignClass}`}>
                      {item.variants[0]?.interestRateDisclaimer?.length > 0 && (
                        <div>
                          <sup>{themeConfig.settings.ratesCard.disclaimerIcons[0]}</sup>{' '}
                          {item.variants[0]?.interestRateDisclaimer}
                        </div>
                      )}
                      {item.variants[0]?.comparisonRateDisclaimer?.length > 0 && (
                        <div>
                          <sup>{themeConfig.settings.ratesCard.disclaimerIcons[1]}</sup>{' '}
                          {item.variants[0]?.comparisonRateDisclaimer}
                        </div>
                      )}
                    </div>
                  )}
                  {item.productDisclaimer?.length > 0 && (
                    <div
                      className={`text-sm mb-6 ${textAlignClass}`}
                      data-rte="true"
                      dangerouslySetInnerHTML={createMarkup(item.productDisclaimer)}
                    ></div>
                  )}
                </div>

                {item.ctaLink?.length ? (
                  <ul className={`flex flex-wrap ${alignment === 'center' ? 'justify-center' : alignment === 'right' ? 'justify-end' : 'justify-start'} gap-4 max-sm:gap-3 mt-6`}>
                    {item.ctaLink
                      .filter((item) => item.link)
                      .map((item, j) => (
                        <li key={j}>
                          <ButtonEl
                            link={item.link}
                            theme={item.linkType.toLowerCase()}
                            className="justify-center! flex!"
                            overrideBase="#ffffff"
                          />
                        </li>
                      ))}
                  </ul>
                ) : null}
                {/* // </li> */}
              </div>
            )
          })}
        </div>
      ) : null}
    </Section>
  )
}

export default RatesCardList
